import React from 'react';

import BoxLayout from 'sharedreact/components/BoxLayout';
import Button from 'sharedreact/components/Button';
import CheckBoxField from 'sharedreact/components/CheckBoxField';
import ColorButtonsField from 'sharedreact/components/ColorButtonsField';
import DropdownField from 'sharedreact/components/DropdownField';
import ModalBackground from 'sharedreact/components/ModalBackground';
import NumberField from 'sharedreact/components/NumberField';
import ParameterComponent from 'sharedreact/components/ParameterComponent';
import ParameterSet from 'sharedreact/components/ParameterSet';
import Row from 'sharedreact/components/Row';
import SlideOut from 'sharedreact/components/SlideOut';
import ValidationForm, { ValidationProps, ValidationWrapper } from 'sharedreact/components/ValidationForm';
import Settings from 'sharedreact/data/Settings';
import UnitSettings from 'sharedreact/data/UnitSettings';
import WarningsSettings from 'sharedreact/data/WarningsSettings';
import { getDisplayLanguage, translate } from 'sharedreact/utils/LanguageSupport';

function getUISizeDisplayName (option: string | null): string | null {
  switch (option) {
    case 'extrasmall':
      return 'Extra Small (Font Size 10px)';
    case 'small':
      return 'Small (Font Size 12px)';
    case 'normal':
      return 'Normal (Font Size 14px)';
  }
  return option;
}

type Props = {
  settings: Settings,
  show: boolean,
  isBusy: boolean,
  newLanguage?: string,
  showUnits?: boolean,

  onChangeValue: (name: string, option: boolean | number | string | WarningsSettings | null, event?: React.FocusEvent<HTMLInputElement> | React.MouseEvent<HTMLLIElement> | React.MouseEvent<HTMLButtonElement> | React.ChangeEvent<HTMLInputElement> | React.SyntheticEvent) => void,
  onClose: () => void,
  onClearPreferences: () => void,
} & ValidationProps;

type DefaultProps = Required<Pick<Props, 'showUnits'>>;

type PropsWithDefaults = Omit<Props, keyof DefaultProps> & DefaultProps;

class SettingsSlideoutImpl extends React.Component<Props> {
  static defaultProps: DefaultProps = {
    showUnits: true
  };

  constructor (props: Props) {
    super(props);

    this.handleClose = this.handleClose.bind(this);
    this.handleChangeUseDefaultTheme = this.handleChangeUseDefaultTheme.bind(this);
    this.handleResetWarnings = this.handleResetWarnings.bind(this);
    this.handleChangeDropDownField = this.handleChangeDropDownField.bind(this);
    this.handleChangeDecimals = this.handleChangeDecimals.bind(this);
    this.handleOnKeyDown = this.handleOnKeyDown.bind(this);
  }

  override componentDidMount (): void {
    if (this.props.show) {
      document.addEventListener('keydown', this.handleOnKeyDown);
    }
  }

  override componentDidUpdate (prevProps: Readonly<Props>): void {
    if (!prevProps.show && this.props.show) {
      document.addEventListener('keydown', this.handleOnKeyDown);
    } else if (prevProps.show && !this.props.show) {
      document.removeEventListener('keydown', this.handleOnKeyDown);
    }
  }

  override componentWillUnmount (): void {
    if (this.props.show) {
      document.removeEventListener('keydown', this.handleOnKeyDown);
    }
  }

  handleOnKeyDown (event: KeyboardEvent): void {
    if (event.keyCode === 80 && event.shiftKey && event.ctrlKey && event.altKey) {
      this.props.onChangeValue('enableDevSettings', !this.props.settings.enableDevSettings, undefined);
    }
  }

  handleClose (): void {
    if (this.props.isFormValid) {
      this.props.onClose();
    }
  }

  handleChangeUseDefaultTheme (name: string, value: boolean) {
    this.props.onChangeValue(name, value ? '' : Settings.ThemeOptions.get(0)!);
  }

  handleResetWarnings (event: React.MouseEvent<HTMLButtonElement>) {
    this.props.onChangeValue('warningsSettings', new WarningsSettings(), event);
  }

  handleChangeDecimals (name: string, value: number | string, event?: React.FocusEvent<HTMLInputElement>) {
    if (this.props.isFormValid) {
      this.props.onChangeValue(name, value, event);
    }
  }

  handleChangeDropDownField (name: string, option: string | null, index: number, event: React.MouseEvent<HTMLLIElement>) {
    this.props.onChangeValue(name, option, event);
  }

  override render (): React.ReactNode {
    const { settings, show, showUnits, isBusy, newLanguage,
      onChangeValue, onClearPreferences, isFormValid } = (this.props as PropsWithDefaults);

    const hasFormErrors: boolean = !isFormValid;

    return (
      <ModalBackground
        visible={show}
        className='esko_settingsslideoutbackground'
        onClick={this.handleClose}>
        <SlideOut id='ae_slideout_settings'
          language={settings.language}
          visible={show}
          className='esko_settingsslideout'
          onClose={this.handleClose}
          canClose={!hasFormErrors && !isBusy}
          alignRight>
          <ValidationForm className='esko_container'>
            <Row>
              <ParameterSet title={translate('shared.settings.title')}>
                <ColorButtonsField id='ae_menu_settings_theme'
                  name='theme'
                  label={translate('shared.settings.theme')}
                  value={settings.theme}
                  options={Settings.ThemeOptions}
                  onChangeValue={onChangeValue}
                />
                <CheckBoxField id='ae_toggle_settings_usebrowsertheme'
                  name='theme'
                  label={translate('shared.settings.usebrowsertheme')}
                  value={settings.theme === ''}
                  disabled={isBusy}
                  onChangeValue={this.handleChangeUseDefaultTheme}
                />
                <DropdownField id='ae_menu_settings_language'
                  label={translate('shared.settings.language')}
                  name='language'
                  value={isBusy && newLanguage != null ? newLanguage : settings.language}
                  options={Settings.LanguageOptions}
                  onChangeValue={this.handleChangeDropDownField}
                  disabled={isBusy}
                  formatOption={(getDisplayLanguage)}
                />
                {showUnits
                  ? <DropdownField id='ae_menu_settings_dimension'
                    label={translate('shared.settings.dimension')}
                    name='units.dimension'
                    value={settings.units.dimension}
                    options={UnitSettings.getDimensionUnits()}
                    disabled={isBusy}
                    onChangeValue={this.handleChangeDropDownField}
                  />
                  : null}
                {showUnits
                  ? <DropdownField id='ae_menu_settings_resolution'
                    label={translate('shared.settings.resolution')}
                    name='units.resolution'
                    value={settings.units.resolution}
                    options={UnitSettings.getResolutionUnits()}
                    disabled={isBusy}
                    onChangeValue={this.handleChangeDropDownField}
                  />
                  : null}
                {showUnits
                  ? <DropdownField id='ae_menu_settings_ruling'
                    label={translate('shared.settings.ruling')}
                    name='units.ruling'
                    value={settings.units.ruling}
                    options={UnitSettings.getRulingUnits()}
                    disabled={isBusy}
                    onChangeValue={this.handleChangeDropDownField}
                  />
                  : null}
                {showUnits
                  ? <DropdownField id='ae_menu_settings_weight'
                    label={translate('shared.settings.weight')}
                    name='units.weight'
                    value={settings.units.weight}
                    options={UnitSettings.getWeightUnits()}
                    disabled={isBusy}
                    onChangeValue={this.handleChangeDropDownField}
                  />
                : null}
                {showUnits
                  ? <NumberField id='ae_menu_settings_decimals'
                    label={translate('shared.settings.decimals')}
                    name='units.decimals'
                    value={settings.units.decimals}
                    onChangeValue={this.handleChangeDecimals}
                    disabled={isBusy}
                    decimals={0}
                    precision={0}
                    greaterThanOrEqualTo={0}
                    lessThan={16}
                  />
                  : null}
                <ParameterComponent
                  inputId='ae_button_settings_reset_warnings'
                  label=''
                  inputContainer={false}>
                  <div className='esko_forminput'>
                    <Button
                      id='ae_button_settings_reset_warnings'
                      onClick={this.handleResetWarnings}
                      disabled={isBusy}
                      title={translate('shared.settings.tooltip.resetwarnings')}>
                      {translate('shared.settings.resetwarnings')}
                    </Button>
                  </div>
                </ParameterComponent>
                {settings.enableDevSettings
                  ? <CheckBoxField id='ae_menu_settings_dev'
                    name='enableDevSettings'
                    value={settings.enableDevSettings}
                    label='Enable Dev Settings'
                    disabled={isBusy}
                    onChangeValue={onChangeValue}
                  />
                  : null}
                {settings.enableDevSettings
                  ? <CheckBoxField id='ae_menu_settings_shouldtranslatetext'
                    name='shouldTranslateText'
                    value={settings.shouldTranslateText}
                    label='Translate All Text'
                    disabled={isBusy}
                    onChangeValue={onChangeValue}
                  />
                  : null}
                {settings.enableDevSettings
                  ? <DropdownField id='ae_menu_settings_font'
                    label='Font'
                    name='font'
                    value={settings.font}
                    options={Settings.Fonts}
                    onChangeValue={this.handleChangeDropDownField}
                    disabled={isBusy}
                    formatOption={Settings.getFontDisplayName}
                  />
                  : null}
                {settings.enableDevSettings
                  ? <DropdownField id='ae_menu_settings_accentColor'
                    label='Accent Color'
                    name='accentColor'
                    value={settings.accentColor}
                    options={Settings.AccentColors}
                    onChangeValue={this.handleChangeDropDownField}
                    disabled={isBusy}
                    formatOption={(value) => (
                      <BoxLayout horizontal>
                        <div className={'esko_colorpatch esko_colorpatch-small esko_colorpatch-round esko_accentcolor_color_' + value} style={{marginRight: 10}} />{value}
                      </BoxLayout>
                    )}
                    />
                  : null}
                {settings.enableDevSettings
                  ? <DropdownField id='ae_menu_settings_uisize'
                    label='UI Size'
                    name='uiSize'
                    value={settings.uiSize}
                    options={Settings.UISizes}
                    onChangeValue={this.handleChangeDropDownField}
                    disabled={isBusy}
                    formatOption={getUISizeDisplayName} />
                  : null}
                {settings.enableDevSettings
                  ? <DropdownField id='ae_menu_settings_loglevel'
                    label='Log Level'
                    name='logLevel'
                    value={settings.logLevel}
                    options={Settings.LogLevels}
                    disabled={isBusy}
                    onChangeValue={this.handleChangeDropDownField} />
                  : null}
                {settings.enableDevSettings
                  ? <CheckBoxField id='ae_menu_settings_logtoconsole'
                    name='logToConsole'
                    value={settings.logToConsole}
                    label='Log To Console'
                    disabled={isBusy}
                    onChangeValue={onChangeValue} />
                  : null}
                {settings.enableDevSettings
                  ? <CheckBoxField id='ae_menu_settings_lograwdata'
                    name='logRawData'
                    value={settings.logRawData}
                    label='Log Raw Data'
                    disabled={isBusy}
                    onChangeValue={onChangeValue} />
                  : null}
                {settings.enableDevSettings
                  ? <CheckBoxField id='ae_menu_settings_logactions'
                    name='logActions'
                    value={settings.logActions}
                    label='Log Actions'
                    disabled={isBusy}
                    onChangeValue={onChangeValue} />
                  : null}
                {settings.enableDevSettings
                  ? <CheckBoxField id='ae_menu_settings_logevents'
                    name='logEvents'
                    value={settings.logEvents}
                    label='Log Events'
                    disabled={isBusy}
                    onChangeValue={onChangeValue} />
                  : null}
                {settings.enableDevSettings
                  ? <CheckBoxField id='ae_menu_settings_logheartbeatevents'
                    name='logHeartBeatEvents'
                    value={settings.logEvents && settings.logHeartBeatEvents}
                    label='Log Heardbeat Events'
                    disabled={!settings.logEvents || isBusy}
                    onChangeValue={onChangeValue} />
                  : null}
                {settings.enableDevSettings
                  ? <CheckBoxField id='ae_menu_settings_logservercalls'
                    name='logServerCalls'
                    value={settings.logServerCalls}
                    label='Log Server Calls'
                    disabled={isBusy}
                    onChangeValue={onChangeValue} />
                  : null}
                {settings.enableDevSettings
                  ? <ParameterComponent
                    inputId='ae_button_settings_reset_warnings'
                    label=''
                    inputContainer={false}>
                    <div className='esko_forminput'>
                      <Button id='ae_button_settings_clearprefs'
                        onClick={onClearPreferences} disabled={isBusy}>Clear Preferences</Button>
                    </div>
                  </ParameterComponent>
                  : null}
                {settings.enableDevSettings
                  ? <CheckBoxField id='ae_menu_settings_showrecorder'
                    name='showRecorder'
                    value={settings.showRecorder}
                    label='Show Server Calls Recorder'
                    disabled={isBusy}
                    onChangeValue={onChangeValue} />
                  : null}
                {settings.enableDevSettings
                  ? <CheckBoxField id='ae_menu_settings_debugbuttons'
                    name='showDebugButtons'
                    value={settings.showDebugButtons}
                    label='Show Debug Buttons'
                    disabled={isBusy}
                    onChangeValue={onChangeValue} />
                  : null}
                {settings.enableDevSettings
                  ? <CheckBoxField id='ae_menu_settings_extendedloadhistory'
                    name='showExtendedLoadHistory'
                    value={settings.showExtendedLoadHistory}
                    label='Show Extended Load History'
                    disabled={isBusy}
                    onChangeValue={onChangeValue} />
                  : null}
              </ParameterSet>
            </Row>
          </ValidationForm>
        </SlideOut>
      </ModalBackground>
    );
  }
}

const SettingsSlideout = ValidationWrapper(SettingsSlideoutImpl);

export default SettingsSlideout;
